export type AccessLevel = "admin" | "normal";

/** User-facing labels for access levels (admin is "Leadership" in the product). */
export const ACCESS_LEVEL_LABELS: Record<AccessLevel, string> = {
  normal: "Team",
  admin: "Leadership",
};

/** Access level granted by an invite code on signup. */
export type InviteAccessLevel = "normal" | "leadership";

export const INVITE_ACCESS_LEVEL_LABELS: Record<InviteAccessLevel, string> = {
  normal: "Team",
  leadership: "Leadership",
};

/** Route keys aligned to dashboard sections in AppMenu / AppRoutes. */
export type RouteKey =
  | "company"
  | "grid.sales"
  | "grid.sdr"
  | "team.dashboard"
  | "explore.sales"
  | "explore.sdr"
  | "targets"
  | "audit"
  | "raw"
  | "admin.invites";

/** Boolean per route per access level — source of truth for UI + route guards. */
export const ROUTE_PERMISSIONS: Record<RouteKey, Record<AccessLevel, boolean>> = {
  company: { normal: true, admin: true },
  "grid.sales": { normal: true, admin: true },
  "grid.sdr": { normal: true, admin: true },
  // Reps see their own roster team only; the team list itself is scoped in
  // warehouse.accessible_team_groups, so this flag just opens the route.
  "team.dashboard": { normal: true, admin: true },
  "explore.sales": { normal: false, admin: true },
  "explore.sdr": { normal: false, admin: true },
  targets: { normal: false, admin: true },
  audit: { normal: false, admin: true },
  raw: { normal: false, admin: true },
  "admin.invites": { normal: false, admin: true },
};

export function canAccessRoute(level: AccessLevel, route: RouteKey): boolean {
  return ROUTE_PERMISSIONS[route][level];
}

export function routeKeyFromPathname(pathname: string): RouteKey | null {
  if (pathname.startsWith("/admin")) return "admin.invites";
  if (pathname.startsWith("/raw")) return "raw";
  if (pathname.startsWith("/audit")) return "audit";
  if (pathname.startsWith("/targets")) return "targets";
  if (pathname.startsWith("/team")) return "team.dashboard";
  if (pathname.startsWith("/explore/sdr")) return "explore.sdr";
  if (pathname.startsWith("/explore")) return "explore.sales";
  if (pathname.startsWith("/company")) return "company";
  if (pathname.startsWith("/grid/sdr")) return "grid.sdr";
  if (pathname.startsWith("/grid")) return "grid.sales";
  return null;
}

export function defaultRouteForLevel(level: AccessLevel): string {
  for (const [route, perms] of Object.entries(ROUTE_PERMISSIONS)) {
    if (perms[level]) {
      switch (route as RouteKey) {
        case "company":
          return "/company/sales";
        case "grid.sales":
          return "/grid/sales";
        case "grid.sdr":
          return "/grid/sdr";
        default:
          break;
      }
    }
  }
  return "/grid/sales";
}

export function accessLevelFromAdmin(isAdmin: boolean): AccessLevel {
  return isAdmin ? "admin" : "normal";
}

/**
 * Access is unresolved until the admin lookup has completed for the *current* user.
 * Without the per-user check, the render where a restored session first appears still
 * carries the signed-out result (normal), and route guards redirect leadership-only
 * deep links before the lookup ever runs.
 */
export function isAccessLoading(params: {
  authLoading: boolean;
  userId: string | null;
  resolvedUserId: string | null;
}): boolean {
  if (params.authLoading) return true;
  if (!params.userId) return false;
  return params.resolvedUserId !== params.userId;
}
